import React, { useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Phone, MapPin, ExternalLink, Globe, ChevronDown, Loader2, Map as MapIcon, Star, Award, Navigation } from 'lucide-react';
import { useLang } from '../i18n';
import { publicApi, resolveImage } from '../api';
import { useFetch } from '../useFetch';
import ActivityMap from '../components/ActivityMap';
import ActivityPhotoCarousel from '../components/ActivityPhotoCarousel';
import FavoriteButton from '../components/FavoriteButton';

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = {
  it: { mon: 'Lun', tue: 'Mar', wed: 'Mer', thu: 'Gio', fri: 'Ven', sat: 'Sab', sun: 'Dom' },
  en: { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' },
  de: { mon: 'Mo', tue: 'Di', wed: 'Mi', thu: 'Do', fri: 'Fr', sat: 'Sa', sun: 'So' },
  fr: { mon: 'Lun', tue: 'Mar', wed: 'Mer', thu: 'Jeu', fri: 'Ven', sat: 'Sam', sun: 'Dim' },
  es: { mon: 'Lun', tue: 'Mar', wed: 'Mié', thu: 'Jue', fri: 'Vie', sat: 'Sáb', sun: 'Dom' }
};

const CTA_LABELS = {
  it: { call: 'Chiama', directions: 'Indicazioni', website: 'Sito web' },
  en: { call: 'Call', directions: 'Directions', website: 'Website' },
  de: { call: 'Anrufen', directions: 'Route', website: 'Webseite' },
  fr: { call: 'Appeler', directions: 'Itinéraire', website: 'Site web' },
  es: { call: 'Llamar', directions: 'Cómo llegar', website: 'Sitio web' }
};

// Star rating (rounded to nearest half star)
function StarRating({ value, count }) {
  if (!value || value <= 0) return null;
  const rounded = Math.round(value * 2) / 2;
  const full = Math.floor(rounded);
  const hasHalf = rounded - full === 0.5;
  return (
    <div className="flex items-center gap-1.5 text-[13px]" data-testid="activity-rating">
      <div className="flex items-center gap-[1px]">
        {[0, 1, 2, 3, 4].map(i => {
          const filled = i < full;
          const half = !filled && i === full && hasHalf;
          return (
            <Star
              key={i}
              size={14}
              className={filled || half ? 'text-[color:var(--yup-primary)]' : 'text-white/25'}
              fill={filled ? 'currentColor' : (half ? 'url(#half-gradient)' : 'none')}
              strokeWidth={1.5}
            />
          );
        })}
      </div>
      <span className="font-semibold text-white/90">{value.toFixed(1)}</span>
      {count > 0 && <span className="text-white/50 text-xs">({count})</span>}
    </div>
  );
}

// Sponsor overlay badge
function SponsorBadge({ act }) {
  const level = act.sponsor_active || act.sponsor_level;
  if (!level || level === 'none') return null;

  if (level === 'recommended') {
    return (
      <div
        className="absolute top-0 left-0 z-10 pl-3 pr-5 py-1.5 text-[10px] font-bold tracking-widest uppercase text-black shadow-lg"
        style={{
          background: 'linear-gradient(135deg, #d4c599 0%, var(--yup-primary) 60%, #b09a72 100%)',
          clipPath: 'polygon(0 0, 100% 0, calc(100% - 12px) 50%, 100% 100%, 0 100%)',
          boxShadow: '0 4px 12px -2px rgba(0,0,0,0.6)'
        }}
        data-testid="badge-recommended"
      >
        <Award size={11} className="inline -mt-0.5 mr-1" />
        Consigliato da YUP
      </div>
    );
  }

  if (level === 'top3' && act.top3_rank) {
    return (
      <div
        className="absolute top-2 left-2 z-10 w-11 h-11 rounded-full flex flex-col items-center justify-center text-black font-bold shadow-lg border-2 border-white/80"
        style={{ background: 'linear-gradient(135deg, var(--yup-primary), #d4c599)' }}
        data-testid={`badge-top3-${act.top3_rank}`}
      >
        <span className="text-lg leading-none">{act.top3_rank}°</span>
        <span className="text-[8px] tracking-widest uppercase leading-none mt-0.5">Top</span>
      </div>
    );
  }

  if (level === 'featured') {
    return (
      <div
        className="absolute top-2 left-2 z-10 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase text-black shadow-md"
        style={{ background: 'linear-gradient(135deg, var(--yup-primary), #d4c599)' }}
        data-testid="badge-featured"
      >
        In evidenza
      </div>
    );
  }
  return null;
}

function ActivityCard({ act, lang }) {
  const [openHours, setOpenHours] = useState(false);
  const [googleReviewsFor, setGoogleReviewsFor] = useState(null);
  const trackedRef = React.useRef(false);
  // Track a 'view' event once when the card is rendered
  React.useEffect(() => {
    if (trackedRef.current || !act?.id) return;
    trackedRef.current = true;
    publicApi.track('view', { activity_id: act.id });
  }, [act?.id]);

  const today = new Date().getDay();
  const todayKey = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][today];
  const todayHours = act.hours?.[todayKey] || '—';
  const isClosed = /chiuso|closed/i.test(todayHours);
  const level = act.sponsor_active || act.sponsor_level;
  const ctaLabels = CTA_LABELS[lang] || CTA_LABELS.en;

  const mapsQuery = act.latitude && act.longitude
    ? `${act.latitude},${act.longitude}`
    : (act.address ? encodeURIComponent(act.address) : null);
  const mapsUrl = mapsQuery ? `https://www.google.com/maps/dir/?api=1&destination=${mapsQuery}` : null;

  const cardBorder = level === 'recommended'
    ? 'border-2 border-[color:var(--yup-primary)]/70 shadow-[0_10px_30px_-10px_rgba(188,172,138,0.5)]'
    : level === 'top3'
    ? 'border border-[color:var(--yup-primary)]/40'
    : '';

  return (
    <article
      id={`act-${act.id}`}
      className={`activity-card fade-in-up relative overflow-hidden ${cardBorder} scroll-mt-24 ${act._preview_draft ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-black/40' : ''}`}
      data-testid={`activity-card-${act.id}`}
      data-sponsor-level={level || 'none'}
    >
      {act._preview_draft && (
        <div className="absolute top-2 left-2 z-30 px-2 py-1 rounded-md bg-amber-500 text-black text-[10px] font-black tracking-widest uppercase shadow-lg" data-testid="draft-preview-badge">
          BOZZA · Anteprima
        </div>
      )}
      <div className="activity-image relative">
        <ActivityPhotoCarousel
          photos={Array.isArray(act.gallery) && act.gallery.length > 0 ? act.gallery : (act.image ? [act.image] : [])}
          cover={act.image}
          alt={act.name}
          attributions={act.photos_source === 'google' ? act.photo_attributions : null}
          testIdPrefix={`activity-${act.id}`}
        />
        <SponsorBadge act={act} />
        <FavoriteButton item={act} />
      </div>
      <div className="activity-body">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="activity-title">{act.name}</h3>
          {act.price_range && <span className="price-badge">{act.price_range}</span>}
        </div>

        {/* Rating — prefer Google */}
        {(() => {
          const rating = act.google_rating ?? act.rating;
          const count = act.google_review_count ?? act.review_count;
          if (!rating || rating <= 0) return null;
          return (
            <div className="mb-2 flex items-center gap-2 flex-wrap">
              <StarRating value={rating} count={count || 0} />
              {act.google_place_id && (
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setGoogleReviewsFor(act); }}
                  data-testid={`google-reviews-btn-${act.id}`}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/10 border border-white/15 text-white/80 hover:bg-white/15 hover:border-[color:var(--yup-primary)]/40 transition"
                  title="Vedi recensioni Google"
                >
                  <MapPin size={9}/> Google
                </button>
              )}
            </div>
          );
        })()}

        <p className="activity-desc">{(act.description?.[lang] || act.description?.en || '')}</p>

        {act.hours && Object.keys(act.hours).length > 0 && (
          <button className="hours-toggle" onClick={() => setOpenHours(o => !o)}>
            <span className={`hours-status ${isClosed ? 'closed' : 'open'}`}>
              <Clock size={14} />
              <span>{isClosed ? 'Chiuso oggi' : `Aperto: ${todayHours}`}</span>
            </span>
            <ChevronDown size={16} className={`transition-transform ${openHours ? 'rotate-180' : ''}`} />
          </button>
        )}
        {openHours && act.hours && (
          <ul className="hours-list">
            {DAY_KEYS.map(d => (
              <li key={d} className={`hours-row ${todayKey === d ? 'today' : ''}`}>
                <span className="day">{DAY_LABELS[lang]?.[d] || d}</span>
                <span className="time">{act.hours[d] || '—'}</span>
              </li>
            ))}
          </ul>
        )}

        {act.address && (
          <div className="activity-meta">
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(act.address)}`} target="_blank" rel="noopener noreferrer" className="meta-item">
              <MapPin size={14} /> <span>{act.address}</span>
            </a>
          </div>
        )}

        {/* CTA buttons — Chiama / Indicazioni / Sito web */}
        {(act.phone || mapsUrl || act.website) && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="activity-cta">
            {act.phone && (
              <a
                href={`tel:${act.phone.replace(/\s/g, '')}`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold bg-[color:var(--yup-primary)] text-black hover:brightness-110 transition"
                onClick={() => publicApi.track('click_call', { activity_id: act.id })}
                data-testid="cta-call"
              >
                <Phone size={13} /> {ctaLabels.call}
              </a>
            )}
            {mapsUrl && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border border-white/20 text-white/90 hover:bg-white/10 transition"
                onClick={() => publicApi.track('click_directions', { activity_id: act.id })}
                data-testid="cta-directions"
              >
                <Navigation size={13} /> {ctaLabels.directions}
              </a>
            )}
            {act.website && (
              <a
                href={act.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border border-white/20 text-white/90 hover:bg-white/10 transition"
                onClick={() => publicApi.track('click_website', { activity_id: act.id })}
                data-testid="cta-website"
              >
                <Globe size={13} /> {ctaLabels.website} <ExternalLink size={10} />
              </a>
            )}
          </div>
        )}
      </div>
      {googleReviewsFor && <GoogleReviewsModal act={googleReviewsFor} onClose={() => setGoogleReviewsFor(null)} />}
    </article>
  );
}

// ============ GOOGLE REVIEWS EMBED MODAL ============
function GoogleReviewsModal({ act, onClose }) {
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const src = key && act.google_place_id
    ? `https://www.google.com/maps/embed/v1/place?key=${key}&q=place_id:${act.google_place_id}&language=it`
    : null;
  const gmapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(act.name)}&query_place_id=${act.google_place_id}`;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose} data-testid="google-reviews-modal">
      <div className="w-full max-w-3xl bg-[#0a0a0a] rounded-2xl border border-[color:var(--yup-primary)]/40 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2 text-white font-semibold">
            <MapPin size={18} className="text-[color:var(--yup-primary)]"/>
            <span className="text-sm sm:text-base">Recensioni Google · {act.name}</span>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white text-2xl leading-none px-2" aria-label="Chiudi">×</button>
        </div>
        <div className="relative w-full" style={{ paddingBottom: '75%' }}>
          {src ? (
            <iframe
              src={src}
              title={`Recensioni Google — ${act.name}`}
              className="absolute inset-0 w-full h-full"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm p-4 text-center">
              Widget Google non disponibile. <br />Apri la scheda direttamente su Google Maps.
            </div>
          )}
        </div>
        <div className="p-3 flex justify-between items-center gap-3 flex-wrap bg-black/40">
          <span className="text-xs text-white/50">Recensioni caricate in diretta da Google</span>
          <a href={gmapsUrl} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-black"
             style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}
             data-testid="open-gmaps-link">
            Apri su Google Maps <ExternalLink size={12}/>
          </a>
        </div>
      </div>
    </div>
  );
}

export default function CategoryDetail() {
  const loc = useLocation();
  const parts = loc.pathname.split('/').filter(Boolean);
  const slug = parts[0];
  const catKey = parts[2];
  const subKey = parts[3] || null;
  const nav = useNavigate();
  const { lang, t } = useLang();

  const { data: city } = useFetch(`city:${slug}`, () => publicApi.city(slug), [slug]);
  const { data: categories } = useFetch('categories', publicApi.categories);
  const { data: allSubs } = useFetch(`subs:${catKey}:${slug}`, () => publicApi.subcategories(catKey, slug), [catKey, slug]);
  const { data: acts, loading } = useFetch(`acts:${slug}:${catKey}:${subKey || 'all'}`, () => publicApi.activities({ city: slug, category: catKey, ...(subKey ? { subcategory: subKey } : {}) }), [slug, catKey, subKey]);
  const { data: allActs } = useFetch(`acts:${slug}:${catKey}`, () => publicApi.activities({ city: slug, category: catKey }), [slug, catKey]);

  // ---- Draft preview mode (admin) ----
  // /..../cosa-vedere/xxx?preview=<activity-id> injects a hidden draft into the
  // list so the admin can see how it will look before publishing. The fetch
  // uses the standard public detail endpoint (drafts ARE returned by id).
  const previewId = new URLSearchParams(loc.search).get('preview');
  const { data: previewAct } = useFetch(
    previewId ? `preview:${previewId}` : null,
    () => (previewId ? publicApi.activity(previewId) : Promise.resolve(null)),
    [previewId]
  );

  // Sparkle landing effect — when user lands here from the Home carousel
  // with a #act-<id> hash, scroll to that card and pulse it in gold briefly
  React.useEffect(() => {
    if (loading || !acts || acts.length === 0) return;
    const hash = loc.hash || '';
    if (!hash.startsWith('#act-')) return;
    // Wait a tick so the animated card enters the DOM
    const id = hash.slice(1); // "act-xxx"
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('yup-landing-highlight');
      // Remove the class after the animation finishes so it can trigger again
      setTimeout(() => el.classList.remove('yup-landing-highlight'), 4600);
    }, 200);
    return () => clearTimeout(timer);
  }, [loading, acts, loc.hash]);

  const [sortMode, setSortMode] = useState('featured');
  const [userPos, setUserPos] = useState(null);
  const [geoErr, setGeoErr] = useState(null);

  const requestLocation = () => {
    if (!navigator.geolocation) { setGeoErr('Geolocalizzazione non disponibile'); return; }
    navigator.geolocation.getCurrentPosition(
      pos => { setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setGeoErr(null); },
      err => setGeoErr('Consenti la geolocalizzazione per ordinare per distanza')
    );
  };

  const haversine = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const toRad = d => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat/2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng/2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  };

  const sortedActs = useMemo(() => {
    if (!acts) return [];
    let arr = [...acts];
    // Inject the previewed draft (if any) at the top so it's easy to spot
    if (previewAct && !arr.some(a => a.id === previewAct.id)) {
      arr = [{ ...previewAct, _preview_draft: true }, ...arr];
    }
    // Sponsored always float to the top regardless of user sort (never lose paid position)
    const isSponsored = a => a.sponsor_active && a.sponsor_active !== 'none';
    const compareBySort = (a, b) => {
      // Preview draft always wins the sort so admin sees it first
      if (a._preview_draft && !b._preview_draft) return -1;
      if (b._preview_draft && !a._preview_draft) return 1;
      if (sortMode === 'name') return (a.name || '').localeCompare(b.name || '');
      if (sortMode === 'rating') return ((b.google_rating ?? b.rating) || 0) - ((a.google_rating ?? a.rating) || 0);
      if (sortMode === 'distance' && userPos) {
        const da = (a.latitude && a.longitude) ? haversine(userPos.lat, userPos.lng, a.latitude, a.longitude) : Infinity;
        const db = (b.latitude && b.longitude) ? haversine(userPos.lat, userPos.lng, b.latitude, b.longitude) : Infinity;
        return da - db;
      }
      return 0; // featured → keep backend order
    };
    arr.sort((a, b) => {
      const sa = isSponsored(a), sb = isSponsored(b);
      if (sa && !sb) return -1;
      if (sb && !sa) return 1;
      return compareBySort(a, b);
    });
    if (sortMode === 'distance' && userPos) {
      arr.forEach(a => {
        if (a.latitude && a.longitude) a._distance_km = haversine(userPos.lat, userPos.lng, a.latitude, a.longitude);
      });
    }
    return arr;
  }, [acts, previewAct, sortMode, userPos]);

  const category = categories?.find(c => c.key === catKey);
  const subs = allSubs || [];
  const subcategory = subs && subKey ? subs.find(s => s.key === subKey) : null;

  const countBySub = useMemo(() => {
    const m = {};
    (allActs || []).forEach(a => { if (a.subcategory) m[a.subcategory] = (m[a.subcategory] || 0) + 1; });
    return m;
  }, [allActs]);

  if (loading || !city || !categories) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-gray-400" size={40} /></div>;
  if (!category) return <div className="py-24 text-center">Not found. <Link to="/" className="text-[color:var(--yup-primary)]">Home</Link></div>;

  const showSubGrid = subs.length > 0 && !subKey && !previewId;

  return (
    <div>
      <section className="relative" style={{ minHeight: '32vh' }}>
        <div className="absolute inset-0">
          <img src={resolveImage(subcategory?.image || category.image)} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/55" />
        </div>
        <div className="relative z-10 flex flex-col items-center justify-center text-white text-center px-4 sm:px-6" style={{ minHeight: '32vh' }}>
          <p className="text-xs sm:text-sm tracking-widest uppercase opacity-90 fade-in-up">{city.full_name?.[lang] || city.name} • {category.label?.[lang] || category.label?.en}</p>
          <h1 className="mt-2 text-2xl sm:text-3xl md:text-5xl font-bold tracking-wider fade-in-up fade-in-up-delay-1">{subcategory ? (subcategory.label?.[lang] || subcategory.label?.en) : (category.label?.[lang] || category.label?.en)}</h1>
          {subcategory && <p className="mt-3 text-xs sm:text-sm text-white/80 max-w-2xl fade-in-up fade-in-up-delay-2">{subcategory.desc?.[lang] || subcategory.desc?.en || ''}</p>}
        </div>
      </section>

      {previewId && (
        <div className="bg-amber-500 text-black" data-testid="preview-mode-banner">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex flex-wrap items-center gap-2 text-sm">
            <span className="font-black tracking-wider uppercase text-xs px-2 py-0.5 rounded bg-black text-amber-400">Anteprima</span>
            <span className="font-semibold">Stai vedendo una bozza non pubblica{previewAct?.name ? `: “${previewAct.name}”` : ''}.</span>
            <span className="opacity-80 hidden sm:inline">Solo tu la vedi finché non la pubblichi dal CMS.</span>
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => nav(loc.pathname, { replace: true })}
              className="text-xs font-semibold underline hover:no-underline"
              data-testid="preview-exit-btn"
            >
              Esci dall'anteprima
            </button>
          </div>
        </div>
      )}

      {subs.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Link to={`/${slug}/cosa-vedere`} className="hover:text-[color:var(--yup-primary)]">{t('whatToSee')}</Link><span>›</span>
            <Link to={`/${slug}/cosa-vedere/${catKey}`} className="hover:text-[color:var(--yup-primary)]">{category.label?.[lang] || category.label?.en}</Link>
            {subcategory && (<><span>›</span><span className="text-[color:var(--yup-primary)] font-semibold">{subcategory.label?.[lang] || subcategory.label?.en}</span></>)}
          </div>
          {subKey && (
            <div className="flex flex-wrap gap-2 mb-6">
              <Link to={`/${slug}/cosa-vedere/${catKey}`} className="chip">Tutte</Link>
              {subs.map(s => (
                <Link key={s.key} to={`/${slug}/cosa-vedere/${catKey}/${s.key}`} className={`chip ${subKey === s.key ? 'chip-active' : ''}`}>
                  {s.label?.[lang] || s.label?.en}
                  {countBySub[s.key] ? <span className="chip-count">{countBySub[s.key]}</span> : null}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-10 md:pb-14">
        {showSubGrid ? (
          <div className="pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {subs.map((sub, idx) => {
                const count = countBySub[sub.key] || 0;
                const Wrapper = count > 0 ? Link : 'div';
                return (
                  <Wrapper key={sub.key} to={count > 0 ? `/${slug}/cosa-vedere/${catKey}/${sub.key}` : undefined} className={`category-tile fade-in-up ${count === 0 ? 'disabled' : ''}`} style={{ animationDelay: `${0.05 + idx * 0.05}s` }}>
                    <img src={resolveImage(sub.image)} alt="" loading="lazy" decoding="async" />
                    <div className="category-overlay" />
                    <div className="category-content">
                      <h3 className="category-title">{sub.label?.[lang] || sub.label?.en}</h3>
                      <p className="category-desc">{sub.desc?.[lang] || sub.desc?.en || ''}</p>
                      {count > 0 ? <span className="count-badge mt-3">{count} attività</span> : <span className="soon-badge mt-3">{t('soon')}</span>}
                    </div>
                  </Wrapper>
                );
              })}
            </div>
          </div>
        ) : (acts && acts.length === 0) ? (
          <div className="text-center py-20"><p className="text-gray-500">Nessuna attività disponibile.</p></div>
        ) : (
          <>
            {/* Sort bar */}
            <div className="pt-2 mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs text-white/60 tracking-wider uppercase">
                {sortedActs.length} attività
              </div>
              <div className="flex items-center gap-3">
                <label htmlFor="sort-select" className="text-xs text-white/60 uppercase tracking-widest">Ordina per</label>
                <select
                  id="sort-select"
                  value={sortMode}
                  onChange={e => { setSortMode(e.target.value); if (e.target.value === 'distance' && !userPos) requestLocation(); }}
                  className="bg-white/[0.04] border border-[color:var(--yup-primary)]/30 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[color:var(--yup-primary)]"
                  data-testid="sort-select"
                >
                  <option value="featured">In evidenza</option>
                  <option value="distance">Vicino a te</option>
                  <option value="rating">Valutazione</option>
                  <option value="name">Nome A-Z</option>
                </select>
              </div>
            </div>
            {sortMode === 'distance' && geoErr && (
              <div className="mb-4 text-xs text-amber-400" data-testid="geo-error">{geoErr}</div>
            )}
            {acts?.some(a => typeof a.latitude === 'number' && typeof a.longitude === 'number') && (
              <div className="pt-1 mb-6">
                <div className="flex items-center gap-2 text-sm text-[color:var(--yup-primary)] mb-3 font-semibold">
                  <MapIcon size={16} /> Mappa attività
                </div>
                <ActivityMap activities={sortedActs} height="320px" />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7 pt-2">
              {sortedActs?.map((act, i) => (<div key={act.id} style={{ animationDelay: `${0.05 + i * 0.06}s` }}><ActivityCard act={act} lang={lang} /></div>))}
            </div>
          </>
        )}
        <div className="mt-10 md:mt-14 flex flex-col sm:flex-row items-center justify-center gap-4">
          {subKey ? (
            <button onClick={() => nav(`/${slug}/cosa-vedere/${catKey}`)} className="inline-flex items-center gap-2 text-[color:var(--yup-primary)] font-semibold hover:underline"><ArrowLeft size={16} /> {category.label?.[lang] || category.label?.en}</button>
          ) : (
            <button onClick={() => nav(`/${slug}/cosa-vedere`)} className="inline-flex items-center gap-2 text-[color:var(--yup-primary)] font-semibold hover:underline"><ArrowLeft size={16} /> {t('whatToSee')}</button>
          )}
          <span className="hidden sm:inline text-gray-300">•</span>
          <button onClick={() => nav(`/${slug}`)} className="inline-flex items-center gap-2 text-gray-500 hover:text-[color:var(--yup-primary)]"><ArrowLeft size={16} /> {city.full_name?.[lang] || city.name}</button>
        </div>
      </section>
    </div>
  );
}
